import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export interface AutoApproveTypeStat {
    type: string;
    label: string;
    model: "Person" | "Event";
    approvedToday: number;
    approvedTotal: number;
    /** Already rated "high" — what the next cron run would approve. */
    waitingCount: number;
    /** Fetched but not yet rated by the AI / exact-match pass. The real upstream queue. */
    backlogCount?: number;
    /** Reached the approval step but nothing could be written to an establishment. */
    blockedCount?: number;
    dailyLimit: number;
    /** Read-only here: the switch is the /bo/services one (serviceKey). */
    enabled: boolean;
    serviceKey: string | null;
}

export interface AutoApprovedRecord {
    recordId: string;
    name: string;
    commercialName?: string | null;
    siren?: string | null;
    establishmentId?: string | null;
    eventAddress?: string | null;
    cityPopulation?: number | null;
    displayName?: string | null;
    placeAddress?: string | null;
    /** The approved payload was deleted by the old boot cleanup: values are the establishment's current ones. */
    fromEstablishment?: boolean;
    dataUrl: string | null;
    title: string | null;
    score: number | null;
    checks: Record<string, boolean> | null;
    snippet: string | null;
    reviewedAt: string | null;
    associatedEventId: string | null;
    companyName: string | null;
    restaurantName: string | null;
    address: string | null;
    isOwnerEmail?: boolean | null;
    /** true = written to the establishment · false = approved but nothing landed ·
     *  null = approved before the audit trail existed, unknown. */
    appliedToEstablishment?: boolean | null;
    /** Establishment fields this approval actually wrote, e.g. ["phone", "address.city"]. */
    appliedPaths?: string[];
    /** Approved by the deterministic exact-match path rather than the AI. */
    isExactMatch?: boolean;
}

export interface AutoApproveListResponse {
    data: AutoApprovedRecord[];
    total: number;
    page: number;
    totalPages: number;
}

export const getAutoApproveStats = async (): Promise<AutoApproveTypeStat[]> => {
    const res = await axios.get(`${API_URL}/admin/auto-approve/stats`, {
        headers: authHeaders(),
    });
    return res.data.data;
};

export const getAutoApproveList = async (
    type: string,
    page = 1,
    limit = 50
): Promise<AutoApproveListResponse> => {
    const res = await axios.get(`${API_URL}/admin/auto-approve/${type}/list`, {
        headers: authHeaders(),
        params: { page, limit },
    });
    return res.data;
};

export const revertAutoApprove = async (type: string, recordId: string): Promise<void> => {
    await axios.post(
        `${API_URL}/admin/auto-approve/${type}/revert/${recordId}`,
        {},
        { headers: authHeaders() }
    );
};

export const requeueAutoApprove = async (type: string): Promise<{ message: string }> => {
    const res = await axios.post(
        `${API_URL}/admin/auto-approve/${type}/requeue`,
        {},
        { headers: authHeaders() }
    );
    return res.data;
};

export const updateAutoApproveConfig = async (
    type: string,
    config: { dailyLimit?: number }
): Promise<void> => {
    await axios.put(`${API_URL}/admin/auto-approve/config/${type}`, config, {
        headers: authHeaders(),
    });
};
